import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const TZ = 'Europe/Madrid';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAY_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

// 🔥 Helper: día actual en zona Madrid (devuelve {year, month, day})
function getTodayMadrid() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const parts = {};
  fmt.formatToParts(new Date()).forEach(p => { if (p.type !== 'literal') parts[p.type] = p.value; });
  return {
    year: parseInt(parts.year),
    month: parseInt(parts.month),
    day: parseInt(parts.day)
  };
}

/**
 * CalendarPicker
 * Props:
 *   onSlotSelected(slotISO, slotLabel) -> callback cuando el usuario elige fecha+hora
 *   embedded (bool) -> si está embebido en iframe, oculta padding extra
 */
function CalendarPicker({ onSlotSelected, embedded = false }) {
  const today = getTodayMadrid();
  const [currentMonth, setCurrentMonth] = useState({ year: today.year, month: today.month });
  const [availableDays, setAvailableDays] = useState(new Set());
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlotISO, setSelectedSlotISO] = useState(null);
  const [error, setError] = useState(null);

  // Cargar disponibilidad del mes
  const loadMonth = useCallback(async (year, month) => {
    setLoadingMonth(true);
    setError(null);
    try {
      const m = String(month).padStart(2, '0');
      const r = await fetch(`${BACKEND_URL}/api/calendar/availability?month=${year}-${m}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error');
      setAvailableDays(new Set((data.days || []).map(d => d.day)));
    } catch (e) {
      setError('Error cargando disponibilidad: ' + e.message);
    } finally {
      setLoadingMonth(false);
    }
  }, []);

  useEffect(() => {
    loadMonth(currentMonth.year, currentMonth.month);
  }, [currentMonth.year, currentMonth.month, loadMonth]);

  // Cargar slots de un día
  const loadSlots = async (year, month, day) => {
    setLoadingSlots(true);
    setSelectedSlotISO(null);
    try {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const r = await fetch(`${BACKEND_URL}/api/calendar/slots?date=${dateStr}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error');
      setSlots(data.slots || []);
    } catch (e) {
      setError('Error cargando horarios: ' + e.message);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDayClick = (day) => {
    if (!availableDays.has(day)) return;
    setSelectedDay(day);
    loadSlots(currentMonth.year, currentMonth.month, day);
  };

  const handleSlotClick = (slot) => {
    setSelectedSlotISO(slot.iso);
    if (onSlotSelected) {
      // Construir label completo: "Lunes 12 de mayo, 13:00"
      const d = new Date(slot.iso);
      const fmt = new Intl.DateTimeFormat('es-ES', {
        timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false
      });
      onSlotSelected(slot.iso, fmt.format(d));
    }
  };

  const goPrevMonth = () => {
    let y = currentMonth.year, m = currentMonth.month - 1;
    if (m < 1) { m = 12; y--; }
    // No permitir ir antes del mes actual
    if (y < today.year || (y === today.year && m < today.month)) return;
    setCurrentMonth({ year: y, month: m });
    setSelectedDay(null);
    setSlots([]);
  };
  const goNextMonth = () => {
    let y = currentMonth.year, m = currentMonth.month + 1;
    if (m > 12) { m = 1; y++; }
    setCurrentMonth({ year: y, month: m });
    setSelectedDay(null);
    setSlots([]);
  };

  const canGoBack = currentMonth.year > today.year || (currentMonth.year === today.year && currentMonth.month > today.month);

  // Render de la grid del calendario
  const renderCalendarGrid = () => {
    const { year, month } = currentMonth;
    const daysInMonth = new Date(year, month, 0).getDate();
    let firstWeekday = new Date(year, month - 1, 1).getDay();
    firstWeekday = firstWeekday === 0 ? 6 : firstWeekday - 1; // L=0..D=6

    const cells = [];
    // Cabeceras
    WEEKDAY_SHORT.forEach((w, i) => {
      cells.push(<div key={`h${i}`} className="cal-head">{w}</div>);
    });
    // Huecos antes del 1
    for (let i = 0; i < firstWeekday; i++) {
      cells.push(<div key={`e${i}`} />);
    }
    // Días
    for (let d = 1; d <= daysInMonth; d++) {
      const isAvailable = availableDays.has(d);
      const isToday = (year === today.year && month === today.month && d === today.day);
      const isSelected = (selectedDay === d);
      const cls = ['cal-day'];
      if (isAvailable) cls.push('available');
      else cls.push('disabled');
      if (isToday) cls.push('today');
      if (isSelected) cls.push('selected');
      cells.push(
        <div
          key={`d${d}`}
          className={cls.join(' ')}
          onClick={() => handleDayClick(d)}
        >
          <span className="cal-day-num">{d}</span>
          {isAvailable && <span className="cal-day-dot" />}
        </div>
      );
    }
    return cells;
  };

  return (
    <div className={`cal-picker ${embedded ? 'embedded' : ''}`}>
      <style>{`
        .cal-picker { font-family: 'DM Sans', sans-serif; color: #fff; padding: ${embedded ? '12px' : '20px'}; max-width: 520px; margin: 0 auto; }
        .cal-picker.embedded { background: transparent; }
        .cal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .cal-header h3 { font-family: 'Space Grotesk', sans-serif; font-size: 18px; margin: 0; flex: 1; text-align: center; }
        .cal-nav-btn { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: #fff; border-radius: 8px; padding: 6px 10px; cursor: pointer; display: flex; align-items: center; }
        .cal-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .cal-nav-btn:hover:not(:disabled) { background: rgba(188,80,218,0.2); border-color: #BC50DA; }
        .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
        .cal-head { font-size: 11px; color: rgba(255,255,255,0.5); text-align: center; padding: 6px 0; font-weight: 600; }
        .cal-day { padding: 10px 4px; text-align: center; border-radius: 8px; cursor: pointer; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); font-size: 13px; position: relative; min-height: 38px; display: flex; flex-direction: column; align-items: center; justify-content: center; transition: all 0.2s; }
        .cal-day.disabled { opacity: 0.25; cursor: not-allowed; }
        .cal-day.available:hover { background: rgba(188,80,218,0.18); border-color: #BC50DA; }
        .cal-day.today { box-shadow: 0 0 0 2px rgba(188,80,218,0.6); border-color: #BC50DA; }
        .cal-day.today .cal-day-num { font-weight: 700; }
        .cal-day.selected { background: linear-gradient(135deg, #BC50DA, #5A1F94); border-color: #BC50DA; font-weight: 600; }
        .cal-day-dot { width: 4px; height: 4px; border-radius: 50%; background: #BC50DA; position: absolute; bottom: 4px; }
        .cal-day.selected .cal-day-dot { background: #fff; }
        .cal-info-row { font-size: 12px; color: rgba(255,255,255,0.6); text-align: center; margin-top: 12px; display: flex; justify-content: center; gap: 14px; flex-wrap: wrap; }
        .cal-info-row span { display: inline-flex; align-items: center; gap: 4px; }
        .cal-slots-wrap { margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.1); }
        .cal-slots-title { font-size: 13px; color: rgba(255,255,255,0.7); margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .cal-slots-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 8px; }
        .cal-slot { padding: 12px 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.15); border-radius: 8px; cursor: pointer; font-size: 14px; text-align: center; transition: all 0.2s; }
        .cal-slot:hover { border-color: #BC50DA; background: rgba(188,80,218,0.12); }
        .cal-slot.selected { background: linear-gradient(135deg, #BC50DA, #5A1F94); border-color: #BC50DA; font-weight: 600; }
        .cal-empty { font-size: 13px; color: rgba(255,255,255,0.5); text-align: center; padding: 16px; }
        .cal-error { background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.3); color: #fca5a5; padding: 10px; border-radius: 8px; font-size: 13px; margin: 12px 0; }
        .cal-loading { text-align: center; padding: 16px; color: rgba(255,255,255,0.5); font-size: 13px; }
      `}</style>

      <div className="cal-header">
        <button className="cal-nav-btn" onClick={goPrevMonth} disabled={!canGoBack}>
          <ChevronLeft size={18} />
        </button>
        <h3>{MONTH_NAMES[currentMonth.month - 1]} {currentMonth.year}</h3>
        <button className="cal-nav-btn" onClick={goNextMonth}>
          <ChevronRight size={18} />
        </button>
      </div>

      {error && <div className="cal-error">{error}</div>}

      <div className="cal-grid">{renderCalendarGrid()}</div>

      <div className="cal-info-row">
        <span><span className="cal-day-dot" style={{ position: 'static' }} /> Día disponible</span>
        <span>● Hoy (Madrid)</span>
      </div>

      {selectedDay && (
        <div className="cal-slots-wrap">
          <div className="cal-slots-title">
            <Clock size={14} /> Horarios disponibles — {selectedDay} de {MONTH_NAMES[currentMonth.month - 1]}
          </div>
          {loadingSlots ? (
            <div className="cal-loading">Cargando horarios…</div>
          ) : slots.length === 0 ? (
            <div className="cal-empty">No hay horarios disponibles este día.</div>
          ) : (
            <div className="cal-slots-grid">
              {slots.map(s => (
                <div
                  key={s.iso}
                  className={`cal-slot ${selectedSlotISO === s.iso ? 'selected' : ''}`}
                  onClick={() => handleSlotClick(s)}
                >
                  {s.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loadingMonth && <div className="cal-loading">Cargando mes…</div>}
    </div>
  );
}

export default CalendarPicker;
